import { Icon, ImageIcon, Loading, Text, Tooltip } from '@/components';
import APP_CONFIG from '@/config/app';
import { IconsName, IconVariants } from '@/interfaces';
import React, {
  cloneElement,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const {
  MAIN_PADDING,
  MAIN_GAP,
  MAIN_ROUNDEDNESS,
  WHOLESTEP_DECREMENT,
  HALFSTEP_DECREMENT,
  TEXT: {
    BIG_HEADING,
    BIG_HEADING_COLOR,
    BIG_SUBHEADING,
    BIG_SUBHEADING_COLOR,
  },
} = APP_CONFIG.STYLES;
const spacingConfig = {
  padding: `p${MAIN_PADDING}`,
  gap: `gap${MAIN_GAP}`,
  headerPadding: 'p-0',
  bodyPadding: 'p-0',
  footerPadding: 'p-0',
  headerGap: `gap${MAIN_GAP}`,
  bodyGap: `gap${MAIN_GAP}`,
  footerGap: `gap${MAIN_GAP}`,
  headerLeftNodeGap: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenHeaderLeftIconAndGroupOfTitleAndSubtitle: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenHeaderLeftTitleAndSubtitle: `gap${MAIN_GAP}${HALFSTEP_DECREMENT}`,
  headerRightNodeGap: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  headerLeftNodeElementsGap: `gap${MAIN_GAP}${HALFSTEP_DECREMENT}`,
};
const roundednessConfig = {
  roundedness: `rounded${MAIN_ROUNDEDNESS}`,
};
const sizingConfig = {
  headerHeight: 'h-xl',
  footerHeight: 'h-auto',
  headerLeftIconSize: 'md' as IconVariants['size'],
};
const colorConfig = {
  backgroundColor: 'bg-primary/5',
  borderColor: 'border border-primary/5',
};
const textConfig = {
  titleSize: BIG_HEADING,
  subTitleSize: BIG_SUBHEADING,
  titleColor: BIG_HEADING_COLOR,
  subTitleColor: BIG_SUBHEADING_COLOR,
};
interface CardProps {
  className?: string;
  children?: ReactNode;
  bordered?: boolean;
}
interface CardSectionProps {
  children?: ReactNode;
  className?: string;
}
interface CardHeaderProps extends CardSectionProps {
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  loading?: boolean;
  loadingOnClick?: () => Promise<void>;
  loadingIsButton?: boolean;
  loadingTooltip?: string;
  title?: string;
  subTitle?: string;
  titleIconName?: IconsName;
  titleIconSize?: IconVariants['size'];
  titleImageSrc?: string;
  shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle?: boolean;
  shouldTitleIconAndTitleMiddleAlign?: boolean;
  rightNodeElements?: ReactNode[];
  leftNodeElements?: ReactNode[];
}
const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<CardSectionProps>;
  Footer: React.FC<CardSectionProps>;
} = ({ className, children, bordered = true }: CardProps) => {
  return (
    <div
      className={`relative flex flex-col ${spacingConfig.padding} 
      ${spacingConfig.gap} 
      ${roundednessConfig.roundedness} 
      ${colorConfig.backgroundColor} 
      ${bordered ? colorConfig.borderColor : ''} 
      ${className} 
      ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {children}
    </div>
  );
};
const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  titleIconName,
  titleImageSrc,
  titleIconSize = sizingConfig.headerLeftIconSize,
  shouldTitleIconAndTitleMiddleAlign = false,
  shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle = false,
  subTitle,
  left,
  center,
  right,
  loading,
  loadingOnClick,
  loadingIsButton = false,
  loadingTooltip,
  rightNodeElements,
  leftNodeElements,
  className,
}: CardHeaderProps) => {
  const [iconHeight, setIconHeight] = useState<number | null>(null);
  const textGroupRef = useRef<HTMLDivElement>(null);
  const [isInternalLoading, setIsInternalLoading] = useState(false);
  const handleLoadingClick = () => {
    setIsInternalLoading(true);
    const fallbackTimer = setTimeout(() => {
      setIsInternalLoading(false);
    }, 5000);
    if (loadingOnClick) {
      const result = loadingOnClick();
      if (result && typeof result.then === 'function') {
        result.finally(() => {
          clearTimeout(fallbackTimer);
          setIsInternalLoading(false);
        });
      }
    }
  };
  const effectiveLoading = loading || isInternalLoading;
  useEffect(() => {
    if (
      shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle &&
      textGroupRef.current
    ) {
      const height = Math.floor(textGroupRef.current.clientHeight);
      setIconHeight(height);
    }
  }, [
    shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle,
    title,
    subTitle,
  ]);
  const finalAlignmentClass =
    shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle
      ? 'items-center'
      : shouldTitleIconAndTitleMiddleAlign
        ? 'items-center'
        : 'items-start';
  const renderElement = (element: ReactNode, isLeft = false) => {
    if (React.isValidElement(element) && element.type === Text && isLeft) {
      return cloneElement(element as ReactElement<any>);
    }
    return element;
  };
  const leftContent =
    left ||
    (title || subTitle || titleIconName ? (
      <div
        className={`flex 
        ${finalAlignmentClass} 
        ${spacingConfig.gapBetweenHeaderLeftIconAndGroupOfTitleAndSubtitle} 
        ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {titleIconName && (
          <Icon
            name={titleIconName}
            intent="primary"
            size={
              shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle
                ? undefined
                : titleIconSize
            }
            sizeAsNumber={
              shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle
                ? iconHeight ?? undefined
                : undefined
            }
            className={borderClassToVisualizeElementsAndSpacing}
          />
        )}
        {titleImageSrc && (
          <ImageIcon
            imageSrc={titleImageSrc}
            alt={title || ''}
            width={
              shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle
                ? iconHeight ?? undefined
                : typeof titleIconSize === 'number'
                  ? titleIconSize
                  : undefined
            }
            height={
              shouldHeaderLeftNodeIconHeightEqualHeightOfGroupOfTitleAndSubtitle
                ? iconHeight ?? undefined
                : typeof titleIconSize === 'number'
                  ? titleIconSize
                  : undefined
            }
            className={borderClassToVisualizeElementsAndSpacing}
          />
        )}
        <div
          ref={textGroupRef}
          className={`flex flex-col 
            ${spacingConfig.gapBetweenHeaderLeftTitleAndSubtitle} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {title && (
            <Text
              value={title}
              size={textConfig.titleSize}
              color={textConfig.titleColor}
              className={borderClassToVisualizeElementsAndSpacing}
            />
          )}
          {subTitle && (
            <Text
              value={subTitle}
              size={textConfig.subTitleSize}
              color={textConfig.subTitleColor}
              className={borderClassToVisualizeElementsAndSpacing}
            />
          )}
        </div>
        {leftNodeElements && leftNodeElements.length > 0 && (
          <div
            className={`flex items-center 
              ${spacingConfig.headerLeftNodeElementsGap}`}
          >
            {leftNodeElements.map((node, index) => (
              <div key={index}>{node}</div>
            ))}
          </div>
        )}
      </div>
    ) : null);
  return (
    <div
      className={`relative flex items-center justify-between 
        ${spacingConfig.headerPadding} 
        ${spacingConfig.headerGap} 
        ${sizingConfig.headerHeight} 
        ${className} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      <div
        className={`flex items-center 
          ${spacingConfig.headerLeftNodeGap} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {renderElement(leftContent, true)}
        {loadingIsButton || effectiveLoading ? (
          loadingTooltip ? (
            <Tooltip
              trigger={
                <div
                  className="cursor-pointer"
                  onClick={!effectiveLoading && loadingOnClick ? handleLoadingClick : undefined}
                >
                  <Loading size="xs" isActive={effectiveLoading} />
                </div>
              }
              align="right"
              width="auto"
            >
              <Text value={loadingTooltip} />
            </Tooltip>
          ) : (
            <div
              className={`cursor-pointer ${effectiveLoading ? 'pointer-events-none' : ''}`}
              onClick={!effectiveLoading && loadingOnClick ? handleLoadingClick : undefined}
            >
              <Loading size="xs" isActive={effectiveLoading} />
            </div>
          )
        ) : null}
      </div>
      <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center pointer-events-none">
        <div className={`relative ${borderClassToVisualizeElementsAndSpacing}`}>
          {renderElement(center)}
        </div>
      </div>
      <div
        className={`flex justify-end items-center 
          ${spacingConfig.headerRightNodeGap} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {right
          ? renderElement(right)
          : rightNodeElements &&
          rightNodeElements.map((node, index) => (
            <div key={index} className="flex flex-row items-center">
              {node}
            </div>
          ))}
      </div>
    </div>
  );
};
Card.Header = CardHeader;
Card.Body = ({ children, className }: CardSectionProps): JSX.Element => {
  return (
    <div
      className={`${spacingConfig.bodyGap} 
      ${spacingConfig.bodyPadding} 
      ${className} 
      ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {children}
    </div>
  );
};
Card.Footer = ({ children, className }: CardSectionProps): JSX.Element => {
  return (
    <div
      className={`flex justify-center items-center ${spacingConfig.footerPadding} 
      ${spacingConfig.footerGap} 
      ${sizingConfig.footerHeight} 
      ${className} 
      ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {children}
    </div>
  );
};
export default Card;
